import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, RotateCcw, Menu } from "lucide-react";
import client, { mediaUrl } from "../../lib/api";
import defaultSymbol from "../../assets/brand/logo-symbol.png";
import AdminLayout from "./AdminLayout";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const SLOTS = [
  ["primaryLogo", "Primary Logo", "Main lotus + service symbol shown in the header."],
  ["compactLogo", "Compact Logo / Symbol", "Smaller version used on mobile (optional — falls back to primary)."],
  ["favicon", "Favicon / Site Icon", "Small icon shown in the browser tab."],
];

const resolve = (u) => (u ? mediaUrl(u) : defaultSymbol);
const EMPTY = { primaryLogo: "", compactLogo: "", favicon: "", trustName: "SRI AKILANDESWARI AMMAN TRUST", tagline: "Compassion • Growth • Service" };

export default function Branding() {
  const [doc, setDoc] = useState({ history: [] });
  const [draft, setDraft] = useState(EMPTY);
  const [dirty, setDirty] = useState(false);

  const load = () => client.get("/admin/branding").then(({ data }) => {
    setDoc(data || {});
    setDraft({
      primaryLogo: data?.primaryLogo || "", compactLogo: data?.compactLogo || "", favicon: data?.favicon || "",
      trustName: data?.trustName || EMPTY.trustName, tagline: data?.tagline || EMPTY.tagline,
    });
    setDirty(false);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const setField = (key, value) => { setDraft((d) => ({ ...d, [key]: value })); setDirty(true); };

  const upload = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(png|svg|webp|jpg|jpeg)$/i.test(file.name)) { toast.error("Use a PNG, SVG or WebP image."); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await client.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField(key, data.url);
      toast.success("Uploaded — preview below. Click Save & Activate to apply.");
    } catch { toast.error("Upload failed. Try a smaller PNG/SVG."); }
  };

  const activate = async () => {
    try { await client.put("/admin/branding", draft); toast.success("Branding saved & activated."); load(); }
    catch { toast.error("Could not activate."); }
  };
  const cancel = () => { load(); };
  const revert = async (i) => { await client.post(`/admin/branding/revert/${i}`); toast.success("Reverted to previous logo."); load(); };

  const sym = resolve(draft.primaryLogo);
  const comp = draft.compactLogo ? mediaUrl(draft.compactLogo) : sym;
  const fav = draft.favicon ? mediaUrl(draft.favicon) : sym;
  const name = draft.trustName;
  const tag = draft.tagline;

  return (
    <AdminLayout title="Logo & Branding">
      <p className="mb-6 max-w-2xl text-sm text-ink-muted">
        Manage the Trust logo, name and tagline. The logo image is the <strong>lotus + service symbol</strong>. The Trust name and tagline are shown as separate website text (never embedded in the image), so they stay editable and responsive.
      </p>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Left: text + uploaders */}
        <div className="space-y-5">
          <div className="rounded-md border border-gold/20 bg-white p-5 space-y-4">
            <div>
              <Label>Trust Name</Label>
              <Input className="mt-1.5" value={name} onChange={(e) => setField("trustName", e.target.value)} data-testid="branding-trustname" />
            </div>
            <div>
              <Label>Tagline</Label>
              <Input className="mt-1.5" value={tag} onChange={(e) => setField("tagline", e.target.value)} data-testid="branding-tagline-input" />
              <p className="mt-1 text-xs text-ink-muted">Recommended: Compassion • Growth • Service</p>
            </div>
          </div>

          {SLOTS.map(([key, label, hint]) => (
            <div key={key} className="rounded-md border border-gold/20 bg-white p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-maroon">{label}</h3>
                  <p className="text-xs text-ink-muted">{hint}</p>
                </div>
                {draft[key] && <img src={mediaUrl(draft[key])} alt={label} className="h-12 w-12 object-contain" />}
              </div>
              <label className="mt-4 inline-flex cursor-pointer items-center gap-2 border border-maroon px-4 py-2 text-xs uppercase tracking-wide text-maroon hover:bg-maroon/5">
                <UploadCloud size={15} /> Upload {label}
                <input type="file" accept=".png,.svg,.webp,.jpg,.jpeg,image/*" className="hidden" data-testid={`upload-${key}`} onChange={(e) => upload(e, key)} />
              </label>
            </div>
          ))}
        </div>

        {/* Right: live previews */}
        <div className="space-y-5">
          <div className="rounded-md border border-gold/20 bg-white p-5" data-testid="branding-preview">
            <div className="eyebrow mb-3">Header Preview (Desktop)</div>
            <div className="flex items-center gap-3 border border-maroon/10 bg-ivory px-4 py-3">
              <img src={sym} alt="header logo" className="h-12 w-12 object-contain" />
              <span className="flex flex-col leading-tight">
                <span className="font-serif text-base font-semibold text-maroon">{name}</span>
                <span className="mt-0.5 text-[11px] tracking-[0.16em] text-gold-dark">{tag}</span>
              </span>
            </div>

            <div className="eyebrow mb-3 mt-6">Mobile Preview</div>
            <div className="flex max-w-[320px] items-center justify-between border border-maroon/10 bg-ivory px-3 py-2">
              <div className="flex items-center gap-2">
                <img src={comp} alt="mobile logo" className="h-9 w-9 object-contain" />
                <span className="flex flex-col leading-tight">
                  <span className="font-serif text-xs font-semibold text-maroon">{name}</span>
                  <span className="text-[9px] tracking-[0.14em] text-gold-dark">{tag}</span>
                </span>
              </div>
              <Menu size={18} className="text-maroon" />
            </div>

            <div className="eyebrow mb-3 mt-6">Footer Preview</div>
            <div className="flex items-center gap-3 bg-maroon px-4 py-3">
              <img src={sym} alt="footer logo" className="h-12 w-12 object-contain" />
              <span className="flex flex-col leading-tight">
                <span className="font-serif text-base font-semibold text-ivory">{name}</span>
                <span className="mt-0.5 text-[11px] tracking-[0.16em] text-gold-light">{tag}</span>
              </span>
            </div>

            <div className="eyebrow mb-3 mt-6">Favicon Preview</div>
            <img src={fav} alt="favicon" className="h-8 w-8 rounded-sm border border-gold/30 object-contain" />
          </div>

          <div className="flex gap-3">
            <button onClick={activate} disabled={!dirty} data-testid="branding-activate" className="flex-1 bg-maroon px-5 py-3 text-xs uppercase tracking-wide text-ivory hover:bg-maroon-dark disabled:opacity-50">Save &amp; Activate</button>
            <button onClick={cancel} disabled={!dirty} data-testid="branding-cancel" className="flex-1 border border-gold/40 px-5 py-3 text-xs uppercase tracking-wide text-maroon disabled:opacity-50">Cancel</button>
          </div>
        </div>
      </div>

      {doc.history?.length > 0 && (
        <div className="mt-10">
          <h3 className="mb-4 font-medium text-maroon">Previous Logos</h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {doc.history.map((h, i) => (
              <div key={i} className="rounded-md border border-gold/20 bg-white p-4" data-testid={`history-${i}`}>
                <img src={resolve(h.primaryLogo)} alt={`previous logo ${i + 1}`} className="mb-3 h-16 w-full object-contain" />
                <div className="text-xs text-ink-muted">Archived {(h.activatedAt || "").slice(0, 10) || "—"}</div>
                <button onClick={() => revert(i)} data-testid={`revert-${i}`} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-maroon hover:underline"><RotateCcw size={13} /> Revert to this</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
